// CategoriasDropdown.js — Painel aberto ao clicar em "TODAS AS CATEGORIAS"
// Estrutura (conforme PDF "Todas as Categorias"):
//   1) Banner em destaque no topo (livro/autor destacado, badge "Em destaque")
//   2) Coluna TUDO (Destaques/Mais Vendidos/Autores/Novidades)
//   3) Coluna LIVROS (subdividida em 3 sub-colunas com categorias)
//   4) Rodapé com 4 cards (Publique / Parcerias / Método / Institucional)
//
// Responsivo:
//   - Desktop: painel "drawer" abaixo da barra, full-width até 1280px centralizado
//   - Mobile : full-screen com scroll vertical e botão de fechar fixo
//
// A posição no desktop NÃO está aqui: topo e altura máxima vêm da classe
// `.painel-categorias` (App.css), porque dependem de --nav-h e --nav-topo, que
// são do cabeçalho e mudam por breakpoint e ao rolar. Repetir a conta em
// Tailwind foi o que produziu o `top-[160px]` que cortava a última linha.
//
// VAI PARA O <body> (PORTAL) DESDE 18/10/2026. O painel e a cortina são
// `fixed`, mas nascem dentro do <header>, e o header passou a deslizar com
// `transform` (a faixa laranja que some ao rolar). Elemento `fixed` dentro de
// um ancestral com transform se posiciona pelo ancestral, e não pela janela:
// o painel ficava preso na altura do cabeçalho (~200px), cortado, e a
// cortina só escurecia o próprio cabeçalho.
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import useHomeConfig from "../../hooks/useHomeConfig";
import { OUTROS_SITES } from "../../config/sites";

// Categorias estáticas (PDF). Quando houver endpoint /categorias-destaque
// elas podem virar dinâmicas — por enquanto refletem o PDF fielmente.
const TUDO = [
  { label: "Destaques",     to: "livros",                    active: true },
  { label: "Mais Vendidos", to: "livros?ordem=populares" },
  { label: "Autores",       to: "autores" },
  { label: "Novidades",     to: "livros?ordem=recentes" },
];

const LIVROS_COLS = [
  ["Todos", "Tecnologia da Informação", "Engenharias"],
  ["Filosofia", "Matemática", "Ajuda e Suporte"],
  ["Esportes", "Economia", "Administração"],
];

const RODAPE = [
  { icon: "bi-megaphone-fill",   titulo: "Publique seu Livro", sub: "Veja como lançar seu livro", to: "publique" },
  // Desativados a pedido: Parcerias, Método e Institucional
  // { icon: "bi-people-fill",      titulo: "Parcerias",          sub: "Veja como lançar seu livro", to: "parcerias" },
  // { icon: "bi-journal-bookmark", titulo: "Método",             sub: "Lorem Ipsum",                to: "metodo" },
  // { icon: "bi-building",         titulo: "Institucional",      sub: "Lorem Ipsum",                to: "institucional" },
];

const CategoriasDropdown = ({ open, onClose }) => {
  const main = process.env.REACT_APP_MAIN;
  const [destaque, setDestaque] = useState(null);
  const API_URL = process.env.REACT_APP_API;
  const config = useHomeConfig();

  // Livro em destaque do banner: o escolhido no CMS (navbar_livro); senão, o 1º ebook.
  useEffect(() => {
    if (!open) return;
    if (config?.navbar_livro) {
      setDestaque(config.navbar_livro);
      return;
    }
    const load = async () => {
      try {
        const r = await fetch(API_URL + "ebook");
        if (!r.ok) return;
        const all = await r.json();
        if (Array.isArray(all) && all.length > 0) setDestaque(all[0]);
      } catch {}
    };
    load();
  }, [open, API_URL, config]);

  // Fecha no ESC
  useEffect(() => {
    if (!open) return;
    const h = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open, onClose]);

  if (!open) return null;

  const linkCat = (rotulo) =>
    main + "livros?categoria=" + encodeURIComponent(rotulo);

  return createPortal(
    <>
      {/* Cortina escura (mobile + desktop) */}
      <div
        onClick={onClose}
        className="fixed inset-0 bg-black/40 z-[100]"
      />

      {/* Painel — em desktop fica logo abaixo do header; em mobile é full-screen.
          No celular ele ocupa a tela inteira (top-0 bottom-0) e rola por dentro;
          o desktop recebe topo e altura de `.painel-categorias`, que os calcula a
          partir da altura real do cabeçalho em vez de um número fixo. */}
      <div
        className="painel-categorias
                   fixed z-[101] left-0 right-0
                   md:max-w-[1280px] md:mx-auto md:rounded-2xl
                   bg-insigne-navy-deep border border-line
                   shadow-[0_24px_60px_rgba(0,0,0,0.55)]
                   overflow-y-auto overscroll-contain"
      >
        {/* Header do painel (mobile: barra com botão fechar) */}
        {/* A BARRA DE FECHAR VALE EM TODA LARGURA desde 11/09/2026.
            Ela era `md:hidden`, e no desktop o unico jeito de sair do painel
            era clicar fora dele — o que ninguem descobre sem tentar. Ela
            tambem da titulo ao painel quando a janela e baixa e ele vira uma
            tela inteira. `sticky` para continuar a mao depois de rolar as
            categorias por dentro. */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b border-line
                        bg-insigne-navy-deep">
          <span className="text-white font-bold uppercase text-sm">Todas as categorias</span>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 border border-line text-white text-xl flex items-center justify-center"
          >
            <i className="bi bi-x" />
          </button>
        </div>

        {/* ========== Banner de destaque ========== */}
        <div className="p-4 md:p-5">
          <Link
            to={destaque ? main + "livros/" + destaque.id : main + "livros"}
            onClick={onClose}
            className="cat-banner relative block rounded-xl overflow-hidden border-2 border-insigne-orange
                       h-[120px] md:h-[140px] bg-insigne-navy-soft"
            style={{
              backgroundImage: destaque?.image ? `url(${API_URL}public/${destaque.image})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            {/* Scrim temático: escuro no tema escuro, claro no tema claro */}
            <div className="absolute inset-0 cat-banner-scrim" />
            <div className="absolute inset-0 flex items-center justify-between px-5 md:px-8">
              <p className="text-white/85 text-sm md:text-base font-semibold max-w-[40%] truncate">
                {destaque?.autores?.[0]?.nome || destaque?.autores?.[0] || "Autor em destaque"}
              </p>
              <div className="text-right">
                <p className="text-white font-bold text-lg md:text-2xl italic leading-tight">
                  {destaque?.titulo || "Livro em destaque"}
                </p>
                <p className="text-white/70 text-xs md:text-sm italic mt-1 line-clamp-2">
                  {destaque?.subtitulo || destaque?.descricao || "Conheça a obra que está em destaque na Insigne Acadêmica"}
                </p>
              </div>
            </div>
            {/* Badge laranja "Em destaque" */}
            <span className="absolute right-0 bottom-0 bg-insigne-orange text-paper text-xs font-bold px-3 py-1 rounded-tl-md">
              Em destaque
            </span>
          </Link>
        </div>

        {/* ========== Grid de colunas ========== */}
        <div className="px-4 md:px-5 pb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6">
            {/* ---------- Coluna TUDO ---------- */}
            <div>
              <h4 className="text-insigne-orange font-bold uppercase text-xs tracking-wider mb-3">
                Tudo
              </h4>
              <ul className="flex flex-col gap-1">
                {TUDO.map((i) => (
                  <li key={i.label}>
                    <Link
                      to={main + i.to}
                      onClick={onClose}
                      className={
                        "block px-3 py-2 rounded-md text-sm transition-colors " +
                        (i.active
                          ? "bg-white/10 text-white font-semibold"
                          : "text-white/85 hover:text-white hover:bg-white/5")
                      }
                    >
                      {i.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* ---------- Coluna LIVROS (3 sub-colunas) ---------- */}
            <div className="md:col-span-3">
              <h4 className="text-insigne-orange font-bold uppercase text-xs tracking-wider mb-3">
                Livros
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-4">
                {LIVROS_COLS.map((col, idx) => (
                  <ul key={idx} className="flex flex-col gap-1">
                    {col.map((cat) => (
                      <li key={cat}>
                        <Link
                          to={linkCat(cat)}
                          onClick={onClose}
                          className="block px-3 py-2 rounded-md text-sm text-white/85
                                     hover:text-white hover:bg-white/5 transition-colors"
                        >
                          {cat}
                        </Link>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          </div>

          {/* ========== Rodapé: 4 cards ========== */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {/* Os outros dois sites da Insigne ficam ao lado do "Publique":
                com o cabeçalho rolado, a faixa laranja (onde eles também
                estão) sai da tela, e este painel continua à mão. */}
            {OUTROS_SITES.map((s) => (
              <a
                key={s.chave}
                href={s.url}
                className="flex items-center gap-3 rounded-xl bg-white/5 border border-line
                           px-4 py-3 hover:bg-white/10 hover:border-insigne-orange/40 transition-all"
              >
                <span className="w-10 h-10 rounded-md bg-insigne-navy-soft flex items-center justify-center
                                 text-white text-lg shrink-0">
                  <i className={`bi ${s.icone}`} />
                </span>
                <div className="min-w-0">
                  <p className="text-white font-bold text-sm">{s.rotulo}</p>
                  <p className="text-white/55 text-xs truncate">{s.resumo}</p>
                </div>
              </a>
            ))}
            {RODAPE.map((r) => (
              <Link
                key={r.titulo}
                to={main + r.to}
                onClick={onClose}
                className="flex items-center gap-3 rounded-xl bg-white/5 border border-line
                           px-4 py-3 hover:bg-white/10 hover:border-insigne-orange/40 transition-all"
              >
                <span className="w-10 h-10 rounded-md bg-insigne-navy-soft flex items-center justify-center
                                 text-white text-lg shrink-0">
                  <i className={`bi ${r.icon}`} />
                </span>
                <div className="min-w-0">
                  <p className="text-white font-bold text-sm">{r.titulo}</p>
                  <p className="text-white/55 text-xs truncate">{r.sub}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CategoriasDropdown;
